import React from 'react';
import { ScreenHelpDrawer, HelpScreenKey } from './ScreenHelpDrawer';
import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ElementType;
  helpScreenKey?: HelpScreenKey;
  actions?: React.ReactNode;
  yearSelector?: React.ReactNode;
  badge?: React.ReactNode;
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon: Icon,
  helpScreenKey,
  actions,
  yearSelector,
  badge,
  className,
}) => {
  return (
    <div className={cn('flex flex-col gap-3 pb-3 border-b border-slate-200', className)}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Left: Icon + Title + Subtitle */}
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <div className="p-2 bg-slate-100 rounded-lg text-slate-900 flex-shrink-0 mt-0.5 sm:mt-0">
              <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-slate-900" />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
                {title}
              </h1>
              {badge && <div>{badge}</div>}
            </div>
            {subtitle && (
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Right: Actions Cluster + Screen Help */}
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap justify-start sm:justify-end flex-shrink-0">
          {actions}
          {helpScreenKey && <ScreenHelpDrawer screenKey={helpScreenKey} />}
        </div>
      </div>

      {/* Optional Contextual Sub-bar (e.g. Reporting Year selector) */}
      {yearSelector && (
        <div className="pt-2 flex items-center justify-between flex-wrap gap-2 border-t border-slate-100">
          {yearSelector}
        </div>
      )}
    </div>
  );
};
